import React, { useEffect, useRef, useState } from 'react';
import collectionChargeService from '../services/collectionChargeService';
import { MODES, labelOf, formatMoney, describeApiError, isMoney } from '../utils/collectionCharge';
import './CollectionChargePanel.css';

const EMPTY_ENTRY = { manualAmount: '', override: false, overrideAmount: '', overrideReason: '', waive: false, waiverReason: '' };

/**
 * Shows the collection charge the server calculates for this bill and lets an authorised user enter, override or
 * waive it. The server decides everything: the controls shown here are a convenience, and the registration endpoint
 * re-checks every value and permission when the bill is saved.
 *
 * onChange({ amount, entry, error, note }) reports the estimated charge, the entry to send with the registration,
 * and whether the current entry is invalid (so the page can block saving).
 */
export const CollectionChargePanel = ({ centreId, visitType, pincode, orderValue, onChange }) => {
  const [entry, setEntry] = useState(EMPTY_ENTRY);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const set = (patch) => setEntry((e) => ({ ...e, ...patch }));

  useEffect(() => {
    const mine = ++seq.current;
    if (!centreId || !(orderValue > 0)) {
      setPreview(null);
      setError(null);
      onChange({ amount: 0, entry: null, error: false, note: null });
      return undefined;
    }

    const timer = setTimeout(async () => {
      const payloadEntry = {};
      if (entry.manualAmount !== '') payloadEntry.manualAmount = entry.manualAmount;
      if (entry.override && entry.overrideAmount !== '') {
        payloadEntry.overrideAmount = entry.overrideAmount;
        payloadEntry.overrideReason = entry.overrideReason;
      }
      if (entry.waive) {
        payloadEntry.waive = true;
        payloadEntry.waiverReason = entry.waiverReason;
      }

      // Cheap client check so obviously bad input never reaches the server.
      const clientProblem =
        (payloadEntry.manualAmount !== undefined && !isMoney(payloadEntry.manualAmount)) ||
        (payloadEntry.overrideAmount !== undefined && !isMoney(payloadEntry.overrideAmount));
      if (clientProblem) {
        setError('Enter a valid amount (0 or more, at most 2 decimals).');
        onChange({ amount: 0, entry: null, error: true, note: null });
        return;
      }

      const body = {
        centreId, visitType, pinCode: pincode || null, orderValue,
        manualAmount: payloadEntry.manualAmount !== undefined ? Number(payloadEntry.manualAmount) : null,
        overrideAmount: payloadEntry.overrideAmount !== undefined ? Number(payloadEntry.overrideAmount) : null,
        overrideReason: payloadEntry.overrideReason || null,
        waive: !!payloadEntry.waive,
        waiverReason: payloadEntry.waiverReason || null,
      };

      setBusy(true);
      try {
        const res = await collectionChargeService.previewBilling(body);
        if (mine !== seq.current) return; // a newer request replaced this one
        setHidden(false);
        setError(null);
        setPreview(res.data);
        const hasEntry = Object.keys(payloadEntry).length > 0;
        onChange({
          amount: Number(res.data.finalAmount) || 0,
          entry: hasEntry ? {
            manualAmount: body.manualAmount, overrideAmount: body.overrideAmount, overrideReason: body.overrideReason,
            waive: body.waive, waiverReason: body.waiverReason,
          } : null,
          error: false,
          note: res.data.note,
        });
      } catch (err) {
        if (mine !== seq.current) return;
        const status = err?.response?.status;
        const text = describeApiError(err, 'Could not calculate the collection charge.');
        if (status === 403 && !Object.keys(payloadEntry).length) {
          setHidden(true); // this user's role has no access to the preview
          onChange({ amount: 0, entry: null, error: false, note: null });
        } else if (status === 422 && /not installed/i.test(text)) {
          setHidden(true); // the feature is not installed on this database
          onChange({ amount: 0, entry: null, error: false, note: null });
        } else {
          setError(text);
          onChange({ amount: 0, entry: null, error: true, note: null });
        }
      } finally {
        if (mine === seq.current) setBusy(false);
      }
    }, 350);

    return () => clearTimeout(timer);
    // onChange is a stable setter from the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centreId, visitType, pincode, orderValue, entry]);

  if (hidden || (!preview && !error)) return null;

  const mode = preview?.mode;
  const showManual = mode === 'MANUAL';
  const showOverride = mode === 'AUTO_OVERRIDE';
  const noRule = preview && !preview.ruleFound;

  return (
    <section className="ccp" aria-label="Collection charge">
      <div className="ccp-head">
        <strong>Collection charge</strong>
        {preview && !noRule && <span className={`ccp-mode ccp-mode--${(mode || '').toLowerCase()}`}>{labelOf(MODES, mode)}</span>}
        {busy && <span className="ccp-busy">Calculating…</span>}
      </div>

      {noRule && <p className="ccp-note">No collection charge applies to this centre and visit type.</p>}

      {preview && !noRule && (
        <div className="ccp-line">
          <label className="ccp-line-label" htmlFor="ccp-amount">Charge amount (₹)</label>
          {showManual && preview.canOverride ? (
            <input id="ccp-amount" className="ccp-input" aria-label="Collection charge amount" inputMode="decimal" placeholder={`Enter up to ${preview.maxManualAmount}`} value={entry.manualAmount} onChange={(e) => set({ manualAmount: e.target.value })} />
          ) : (
            <input id="ccp-amount" className="ccp-input is-locked" aria-label="Collection charge (locked)" readOnly tabIndex={-1} value={Number(preview.finalAmount).toFixed(2)} />
          )}
          <span className="ccp-line-hint">
            {showManual && preview.canOverride && `Manual: type the amount (max ${formatMoney(preview.maxManualAmount)})`}
            {showManual && !preview.canOverride && 'Manual: an authorised user must enter the amount.'}
            {!showManual && mode !== 'NO_CHARGE' && '🔒 Filled automatically from the centre rule'}
            {mode === 'NO_CHARGE' && '🔒 No charge for this visit type'}
          </span>
        </div>
      )}
      {preview && !noRule && (
        <div className="ccp-amounts">
          <span>Calculated {formatMoney(preview.calculatedAmount)}</span>
          <strong>Charge {formatMoney(preview.finalAmount)}</strong>
          {preview.waivedAmount > 0 && <span className="ccp-waived">Waived {formatMoney(preview.waivedAmount)}</span>}
        </div>
      )}
      {preview?.note && <p className="ccp-note">{preview.note}</p>}

      {showOverride && preview.canOverride && (
        <>
          <label className="ccp-check">
            <input type="checkbox" aria-label="Override charge" checked={entry.override} onChange={(e) => set({ override: e.target.checked, overrideAmount: e.target.checked ? entry.overrideAmount : '', overrideReason: e.target.checked ? entry.overrideReason : '' })} />
            Override the calculated charge
          </label>
          {entry.override && (
            <div className="ccp-row">
              <label className="ccp-field">Override amount (max {formatMoney(preview.maxManualAmount)})
                <input aria-label="Override amount" inputMode="decimal" value={entry.overrideAmount} onChange={(e) => set({ overrideAmount: e.target.value })} />
              </label>
              <label className="ccp-field">Reason (required)
                <input aria-label="Override reason" maxLength={200} value={entry.overrideReason} onChange={(e) => set({ overrideReason: e.target.value })} />
              </label>
            </div>
          )}
        </>
      )}

      {preview?.allowWaiver && preview.canWaive && (
        <>
          <label className="ccp-check">
            <input type="checkbox" aria-label="Waive charge" checked={entry.waive} onChange={(e) => set({ waive: e.target.checked, waiverReason: e.target.checked ? entry.waiverReason : '' })} />
            Waive the collection charge
          </label>
          {entry.waive && (
            <label className="ccp-field">Waiver reason (required)
              <input aria-label="Waiver reason" maxLength={200} value={entry.waiverReason} onChange={(e) => set({ waiverReason: e.target.value })} />
            </label>
          )}
        </>
      )}

      {error && <p className="ccp-error" role="alert">{error}</p>}
    </section>
  );
};

export default CollectionChargePanel;
